import { createContext, useContext, useEffect, useMemo, useState, Dispatch, SetStateAction, ReactNode } from "react";
import { tryRefreshToken, getTokenExpMs, redirectToLogin } from "../api/base";

interface AuthContextType {
    token: string | null;
    name: string | null;
    email: string | null;
    title: string | null;
    avatar: string | null;
    access: string | null;

    setToken: Dispatch<SetStateAction<string | null>>;
    setUser: Dispatch<SetStateAction<any | null>>;
  }

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = ({ children }:{children: ReactNode}) => {
  const [token, setToken_] = useState(localStorage.getItem("token"));
  const [name, setName_] = useState(localStorage.getItem("name"));
  const [email, setEmail_] = useState(localStorage.getItem("email"));
  const [title, setTitle_] = useState(localStorage.getItem("title"));
  const [avatar, setAvatar_] = useState(localStorage.getItem("avatar"));
  const [access, setAccess_] = useState(localStorage.getItem("access"));
  const [user, setUser_] = useState(localStorage.getItem("user"))

  const setToken = (newToken) => {
    setToken_(newToken);
  };
  const setUser = (newUser) => {
    setName_(newUser.name);
    setEmail_(newUser.email);
    setTitle_(newUser.title);
    setAvatar_(newUser.avatar);
    setAccess_(newUser.access);
    setUser_('true');
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
    if(user){
      localStorage.setItem("name", name);
      localStorage.setItem("email", email);
      localStorage.setItem("title", title);
      localStorage.setItem("avatar", avatar);
      localStorage.setItem("access", access);
      localStorage.setItem("user", user);
    } else {
      localStorage.removeItem("name");
      localStorage.removeItem("email");
      localStorage.removeItem("title");
      localStorage.removeItem("avatar");
      localStorage.removeItem("access");
      localStorage.removeItem("user");
    }
  }, [token, user, name, email, title, avatar, access]);

  useEffect(() => {
    const handler = () => {
      const newToken = localStorage.getItem('token');
      if (newToken && newToken !== token) {
        setToken_(newToken);
      }
    };
    window.addEventListener('token-refreshed', handler);
    return () => window.removeEventListener('token-refreshed', handler);
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const expMs = getTokenExpMs();
    if (!expMs) return;

    const REFRESH_BEFORE_MS = 5 * 60 * 1000;
    const delay = expMs - Date.now() - REFRESH_BEFORE_MS;

    if (delay <= 0) {
      tryRefreshToken();
      return;
    }

    const timerId = setTimeout(() => {
      tryRefreshToken();
    }, delay);

    return () => clearTimeout(timerId);
  }, [token]);

  const contextValue = useMemo(
    () => ({
      token,
      name,
      title,
      email,
      avatar,
      access,
      setToken,
      setUser
    }),
    [token, name, title, email, avatar, access]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
